import { create } from 'zustand';

/**
 * Draft Mode Store
 *
 * Manages the local state for agentic draft sessions.
 * Remote entries arrive via Firestore onSnapshot (set by useDraftSession hook).
 * User overrides are written locally first, then debounce-synced to Firestore.
 * The merged view (remote + local overrides) is what the UI renders.
 */
const useDraftModeStore = create((set, get) => ({
  // Session metadata
  sessionId: null,
  sessionType: null, // e.g. 'timetableAssignments'
  sessionContext: null, // e.g. { timetableId, classId }
  status: 'idle', // 'idle' | 'active' | 'committing' | 'committed' | 'discarded'

  // Entries from Firestore (written by agent or synced user edits)
  remoteEntries: {},

  // Local user overrides not yet synced to Firestore
  localOverrides: {},

  // Entries marked for deletion
  localDeletions: new Set(),

  // Agent progress display
  agentProgress: {
    status: 'idle', // 'idle' | 'working' | 'paused' | 'done' | 'error'
    currentStep: '',
    stepsCompleted: 0,
    stepsTotal: 0,
  },

  // Debounce timer for syncing local overrides back to Firestore
  _syncTimer: null,

  /**
   * Merged view: remote entries + local overrides - deletions.
   * This is what the UI should render.
   */
  getMergedEntries: () => {
    const { remoteEntries, localOverrides, localDeletions } = get();
    const merged = { ...remoteEntries };

    // Apply local overrides on top
    Object.entries(localOverrides).forEach(([id, data]) => {
      merged[id] = { ...merged[id], ...data, _source: 'user' };
    });

    // Remove deletions
    localDeletions.forEach(id => {
      delete merged[id];
    });

    return merged;
  },

  /**
   * Get merged entries as an array (for components expecting arrays)
   */
  getMergedEntriesArray: () => {
    const merged = get().getMergedEntries();
    return Object.entries(merged).map(([id, data]) => ({ id, ...data }));
  },

  // --- Actions ---

  /**
   * Start a new draft session
   */
  startSession: (sessionId, type, context) => {
    set({
      sessionId,
      sessionType: type,
      sessionContext: context,
      status: 'active',
      remoteEntries: {},
      localOverrides: {},
      localDeletions: new Set(),
      agentProgress: {
        status: 'idle',
        currentStep: '',
        stepsCompleted: 0,
        stepsTotal: 0,
      },
    });
  },

  /**
   * Update remote entries from Firestore snapshot
   * Called by useDraftSession when onSnapshot fires
   */
  setRemoteEntries: (entries) => {
    set({ remoteEntries: entries });
  },

  /**
   * Update agent progress from Firestore snapshot
   */
  setAgentProgress: (progress) => {
    set({ agentProgress: { ...get().agentProgress, ...progress } });
  },

  /**
   * Add a local user override (instant UI update, queued for Firestore sync)
   */
  setLocalOverride: (entryId, data) => {
    set(state => ({
      localOverrides: {
        ...state.localOverrides,
        [entryId]: { ...state.localOverrides[entryId], ...data },
      },
      // Remove from deletions if re-adding
      localDeletions: (() => {
        const next = new Set(state.localDeletions);
        next.delete(entryId);
        return next;
      })(),
    }));
  },

  /**
   * Mark an entry for local deletion
   */
  setLocalDeletion: (entryId) => {
    set(state => {
      const nextOverrides = { ...state.localOverrides };
      delete nextOverrides[entryId];
      const nextDeletions = new Set(state.localDeletions);
      nextDeletions.add(entryId);
      return { localOverrides: nextOverrides, localDeletions: nextDeletions };
    });
  },

  /**
   * Clear local overrides for entries that have been synced to Firestore
   */
  clearSyncedOverrides: (entryIds) => {
    set(state => {
      const nextOverrides = { ...state.localOverrides };
      entryIds.forEach(id => delete nextOverrides[id]);
      return { localOverrides: nextOverrides };
    });
  },

  /**
   * Clear synced deletions
   */
  clearSyncedDeletions: (entryIds) => {
    set(state => {
      const next = new Set(state.localDeletions);
      entryIds.forEach(id => next.delete(id));
      return { localDeletions: next };
    });
  },

  /**
   * Set session status
   */
  setStatus: (status) => {
    set({ status });
  },

  /**
   * Get summary for pre-commit display
   */
  getCommitSummary: () => {
    const merged = get().getMergedEntries();
    const entries = Object.values(merged);
    const agentCount = entries.filter(e => e._source === 'agent').length;
    const userCount = entries.filter(e => e._source === 'user').length;
    return {
      totalEntries: entries.length,
      agentEntries: agentCount,
      userEntries: userCount,
      deletions: get().localDeletions.size,
    };
  },

  /**
   * Reset store to idle state
   */
  reset: () => {
    const { _syncTimer } = get();
    if (_syncTimer) clearTimeout(_syncTimer);
    set({
      sessionId: null,
      sessionType: null,
      sessionContext: null,
      status: 'idle',
      remoteEntries: {},
      localOverrides: {},
      localDeletions: new Set(),
      agentProgress: {
        status: 'idle',
        currentStep: '',
        stepsCompleted: 0,
        stepsTotal: 0,
      },
      _syncTimer: null,
    });
  },
}));

export default useDraftModeStore;
